import { useState, useEffect, Fragment } from "react";
import { useLocation, Link } from "react-router-dom";
import { SCHOOL, NAV } from "data";
import { useSetPage, idForPath } from "lib/nav";
import { useResource } from "hooks/useResource";
import { getSettings, getInstitutions, getAdmissionRounds } from "services/content";
import { admissionState } from "pages/admissions/admissionRouting";
import { mediaUrl } from "lib/media";
import { buttonVariants } from "components/ui/button";
import { cn } from "lib/utils";

/**
 * Header CTA linking out to the ConnectEd portal.
 * The URL comes from admin → Settings → ConnectEd Portal (settings.connectedUrl).
 * When it isn't configured we render nothing at all rather than a dead button —
 * a header CTA that goes nowhere is worse than no CTA.
 */
function ConnectEdButton({ className, size = "sm", onNavigate, label = "Login" }) {
  const { data: settings } = useResource(getSettings);
  const url = settings?.connectedUrl;
  if (!url) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={onNavigate}
      className={cn(buttonVariants({ variant: "gold", size }), "no-underline", className)}
    >
      🎓 {label}
    </a>
  );
}

/**
 * The school is admitting in person for now, so the site does not advertise
 * admissions anywhere: the NAV entry carries `hidden`, the footer's quick link
 * is out, and this switch takes out the header button and its mobile
 * counterpart. Set it to true to bring both back.
 */
const SHOW_ADMISSION_CTA = false;

/**
 * Header link to the admissions page of whichever institution is actually
 * taking applications right now.
 *
 * It disappears completely when no round is open, rather than leading a parent
 * to a page that only says "Admissions closed". The public rounds endpoint
 * already returns active rounds only; admissionState additionally honours the
 * acceptance switch and the opening and closing dates.
 */
function AdmissionButton({ className, size = "sm", onNavigate }) {
  const { data: rounds } = useResource(getAdmissionRounds);
  const open = (rounds || []).find((r) => admissionState(r) === "open");
  if (!SHOW_ADMISSION_CTA || !open) return null;

  return (
    <Link
      to={`/admissions/${open.institution}`}
      onClick={onNavigate}
      className={cn(
        buttonVariants({ variant: "outline", size }),
        // The header bar is deep green, so the green outline style would vanish.
        "no-underline bg-white border-white text-green-dark hover:bg-cream",
        className
      )}
    >
      <span className="font-bn">ভর্তি</span> / Admission
    </Link>
  );
}

/**
 * Floating ConnectEd button for narrow screens.
 * Below `sm` the header CTA is hidden to make room for the logo + hamburger,
 * so this floats it directly beneath the header, over the top of the hero.
 * It tracks the header's height: the utility bar (~26px) scrolls away while the
 * header itself is sticky, so the offset shrinks once the page is scrolled.
 * Hidden while the drawer is open — the drawer has its own ConnectEd button.
 */
function ConnectEdFab({ hidden, scrolled }) {
  return (
    <div
      className="sm:hidden fixed right-4 z-[900] transition-all duration-300"
      style={{
        top: scrolled ? 82 : 108,
        opacity: hidden ? 0 : 1,
        pointerEvents: hidden ? "none" : "auto",
      }}
    >
      <ConnectEdButton className="rounded-full shadow-[0_6px_20px_rgba(0,0,0,0.35)] ring-2 ring-white/25" />
    </div>
  );
}

/**
 * Floating Admission button for narrow screens — the left-hand counterpart of
 * ConnectEdFab, which floats on the right. Same offsets so the two sit level.
 * Like the header version it renders nothing at all when no round is open.
 */
function AdmissionFab({ hidden, scrolled }) {
  return (
    <div
      className="sm:hidden fixed left-4 z-[900] transition-all duration-300"
      style={{
        top: scrolled ? 82 : 108,
        opacity: hidden ? 0 : 1,
        pointerEvents: hidden ? "none" : "auto",
      }}
    >
      <AdmissionButton className="rounded-full shadow-[0_6px_20px_rgba(0,0,0,0.35)] ring-2 ring-white/25" />
    </div>
  );
}

/** Institution links inside the mobile drawer — always expanded, no dropdown. */
function MobileInstitutions({ onNavigate }) {
  const { data } = useResource(getInstitutions);
  const items = data?.items || [];
  if (!items.length) return null;

  return (
    <div className="mt-3 pt-3 border-t border-white/10">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.13em] text-white/40 px-4 mb-1.5">
        Institutions
      </div>
      {items.map((it) => (
        <Link
          key={it.slug}
          to={`/institutions/${it.slug}`}
          onClick={onNavigate}
          className="block px-4 py-2.5 rounded-lg text-[14px] text-[#c0e0d4] no-underline"
        >
          {it.name}
        </Link>
      ))}
    </div>
  );
}

function Logo({ onClick }) {
  const { data: settings } = useResource(getSettings);
  const logo = mediaUrl(settings?.logo);
  const name = settings?.profile?.name || SCHOOL.name;
  const college = settings?.profile?.college || SCHOOL.college;

  return (
    <div
      onClick={onClick}
      className="flex items-center gap-3.5 cursor-pointer shrink-0"
    >
      <div className="w-[50px] h-[50px] bg-white rounded-full flex items-center justify-center overflow-hidden shrink-0">
        {logo ? (
          <img src={logo} alt={name} className="w-full h-full object-cover" />
        ) : (
          <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden="true">
            <circle cx="18" cy="18" r="16" fill="#005C40" />
            <ellipse cx="18" cy="16" rx="7" ry="7" fill="#B8002B" />
            <path d="M9 28 Q18 21 27 28" stroke="#f7efc8" strokeWidth="1.8" fill="none" />
            <line x1="18" y1="9" x2="18" y2="12" stroke="#f7efc8" strokeWidth="1.5" />
          </svg>
        )}
      </div>
      <div>
        <div className="text-white font-serif text-[15px] font-bold leading-[1.25]">
          {name}
        </div>
        <div className="text-white font-serif text-[15px] font-bold leading-[1.25]">
          &amp; {college}
        </div>
        <div className="text-gold-light text-[10px] sm:text-[10.5px] font-semibold leading-tight mt-0.5">
          {SCHOOL.managedBy}
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Utility-bar contact details: admin → Settings → School Profile, with the
  // static SCHOOL constants as the fallback.
  const { data: settings } = useResource(getSettings);
  const p = settings?.profile || {};
  const setPage = useSetPage();
  const location = useLocation();
  const page = idForPath(location.pathname);
  // Full route id ("academics/kcns") so submenu entries can show as current.
  const fullId = location.pathname.replace(/^\/+|\/+$/g, "") || "home";

  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);

  // Lock body scroll + allow Escape to close the mobile drawer.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const goto = (id) => {
    setMenuOpen(false);
    setPage(id);
  };


  return (
    <>
      {/* Top utility bar */}
      <div className="bg-green-dark px-4 sm:px-10 py-1.5 flex justify-between text-[11px] sm:text-xs text-[#a0c8b8] gap-4">
        <span className="truncate">📍 {p.address || SCHOOL.address}</span>
        <span className="hidden sm:flex gap-5">
          <span>📞 {p.phone || SCHOOL.phone}</span>
          <span>✉ {p.email || SCHOOL.email}</span>
        </span>
      </div>

      <header
        className="sticky top-0 z-[1000] transition-all duration-300"
        style={{
          background: scrolled ? "rgba(0,60,43,0.97)" : "#005C40",
          backdropFilter: scrolled ? "blur(12px)" : "none",
          boxShadow: scrolled ? "0 2px 20px rgba(0,0,0,0.3)" : "none",
        }}
      >
        <div className="max-w-container mx-auto px-4 sm:px-10 h-[70px] flex items-center justify-between gap-4">
          <Logo onClick={() => goto("home")} />

          {/* Desktop nav */}
          <nav className="hidden lg:flex gap-0.5 items-center" aria-label="Primary">
            {NAV.filter((n) => !n.hidden).map(({ id, label, children }) => {
              const on = page === id;
              const itemCls = cn(
                "font-sans text-[13.5px] px-[13px] py-1.5 rounded-t-md cursor-pointer transition-all duration-200 tracking-[0.01em] border-b-2 bg-transparent",
                on
                  ? "text-[#f7efc8] border-[#f7efc8] font-semibold"
                  : "text-[#c0e0d4] border-transparent font-normal hover:text-[#f7efc8] hover:border-[#f7efc8]/60 hover:bg-white/[0.06]"
              );

              // Entries with children open a hover/focus dropdown. The parent
              // itself does NOT navigate (the school asked for this): it only
              // opens the submenu, and clicking it focuses it so the dropdown
              // also opens on touch/keyboard via group-focus-within.
              if (children?.length) {
                return (
                  <div key={id} className="relative group">
                    <button
                      type="button"
                      aria-haspopup="true"
                      className={cn(itemCls, "inline-flex items-center gap-1 cursor-default")}
                    >
                      {label}
                      <span aria-hidden="true" className="text-[9px] leading-none opacity-80">
                        &#9660;
                      </span>
                    </button>
                    <div
                      className="absolute left-0 top-full pt-2 min-w-[230px] opacity-0 invisible translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0"
                    >
                      <div className="bg-white rounded-xl shadow-[0_18px_40px_rgba(0,0,0,0.22)] border border-black/5 overflow-hidden py-1.5">
                        {children.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => goto(c.id)}
                            aria-current={fullId === c.id ? "page" : undefined}
                            className={cn(
                              "w-full text-left px-4 py-2.5 bg-transparent border-none cursor-pointer transition-colors hover:bg-green-light",
                              fullId === c.id && "bg-green-light"
                            )}
                          >
                            <span className="block text-[13.5px] font-semibold text-green-dark">
                              {c.label}
                            </span>
                            {c.note && (
                              <span className="block text-[11.5px] text-muted mt-0.5">
                                {c.note}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <button key={id} onClick={() => goto(id)} aria-current={on ? "page" : undefined} className={itemCls}>
                  {label}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <AdmissionButton className="hidden sm:inline-flex shrink-0" />
            <ConnectEdButton className="hidden sm:inline-flex shrink-0" />

            {/* Hamburger (mobile/tablet) */}
            <button
              onClick={() => setMenuOpen((o) => !o)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-[5px] rounded-md bg-white/10 border border-white/15"
            >
              <span
                className="block w-5 h-[2px] bg-white rounded-full transition-all duration-300"
                style={{ transform: menuOpen ? "translateY(7px) rotate(45deg)" : "none" }}
              />
              <span
                className="block w-5 h-[2px] bg-white rounded-full transition-all duration-200"
                style={{ opacity: menuOpen ? 0 : 1 }}
              />
              <span
                className="block w-5 h-[2px] bg-white rounded-full transition-all duration-300"
                style={{ transform: menuOpen ? "translateY(-7px) rotate(-45deg)" : "none" }}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className="fixed inset-0 z-[1100] lg:hidden"
        style={{ pointerEvents: menuOpen ? "auto" : "none" }}
      >
        {/* Backdrop */}
        <div
          onClick={() => setMenuOpen(false)}
          className="absolute inset-0 bg-black/50 transition-opacity duration-300"
          style={{ opacity: menuOpen ? 1 : 0 }}
        />
        {/* Panel */}
        <nav
          aria-label="Mobile"
          className="absolute top-0 right-0 h-full w-[78%] max-w-[320px] bg-green-dark shadow-2xl flex flex-col transition-transform duration-300 ease-out"
          style={{ transform: menuOpen ? "translateX(0)" : "translateX(100%)" }}
        >
          <div className="flex items-center justify-between px-6 h-[70px] border-b border-white/10">
            <span className="text-gold-light font-serif font-bold text-lg">
              Menu
            </span>
            <button
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="w-9 h-9 rounded-full bg-white/10 text-white text-xl flex items-center justify-center"
            >
              ×
            </button>
          </div>
          <div className="flex flex-col p-4 gap-1">
            {NAV.filter((n) => !n.hidden).map(({ id, label, children }) => {
              const on = page === id;
              return (
                <Fragment key={id}>
                  {children?.length ? (
                    // Group heading only — not a link; the entries below navigate.
                    <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-[0.13em] text-white/45">
                      {label}
                    </div>
                  ) : (
                    <button
                      onClick={() => goto(id)}
                      aria-current={on ? "page" : undefined}
                      className="text-left px-4 py-3 rounded-lg text-[15px] font-medium transition-colors"
                      style={{
                        background: on ? "rgba(247,239,200,0.12)" : "transparent",
                        color: on ? "#f7efc8" : "#c0e0d4",
                      }}
                    >
                      {label}
                    </button>
                  )}
                  {children?.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => goto(c.id)}
                      aria-current={fullId === c.id ? "page" : undefined}
                      className="text-left px-4 py-2.5 rounded-lg text-[14px] transition-colors"
                      style={{
                        background: fullId === c.id ? "rgba(247,239,200,0.12)" : "transparent",
                        color: fullId === c.id ? "#f7efc8" : "#c0e0d4",
                      }}
                    >
                      {c.label}
                    </button>
                  ))}
                </Fragment>
              );
            })}
            <MobileInstitutions onNavigate={() => setMenuOpen(false)} />
            <AdmissionButton
              size="block"
              className="mt-3"
              onNavigate={() => setMenuOpen(false)}
            />
            <ConnectEdButton
              size="block"
              className="mt-3"
              onNavigate={() => setMenuOpen(false)}
            />
          </div>
        </nav>
      </div>

      <AdmissionFab hidden={menuOpen} scrolled={scrolled} />
      <ConnectEdFab hidden={menuOpen} scrolled={scrolled} />
    </>
  );
}
